/**
 * 用户表单对话框
 */
import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, Radio, TreeSelect, type TreeSelectProps } from 'antd';
import { message } from '@/utils/antdStatic';
import { createUser, updateUser } from '../../api/user';
import { getAssignableRoleOptions } from '../../api/role';
import type { User, UserFormData } from '../../api/types';
import { UserGender, UserStatus } from '../../api/types';

export interface UserFormProps {
  visible: boolean;
  user: User | null;
  onCancel: () => void;
  onSuccess: () => void;
  /** 组织树数据（由父页面预加载传入） */
  orgTreeData?: TreeSelectProps['treeData'];
}

const UserForm: React.FC<UserFormProps> = ({
  visible,
  user,
  onCancel,
  onSuccess,
  orgTreeData = [],
}) => {
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [roleOptions, setRoleOptions] = useState<{ value: number; label: string }[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [roleLoadError, setRoleLoadError] = useState(false);
  const isEdit = !!user;

  // 加载可分配角色：角色统一由根租户控制，后端按操作者租户类型穿透+主体过滤
  // （根租户→internal_user 内部角色；非根租户→根租户 customer 角色）。前端不做身份判断。REQ-20260710-0018。
  useEffect(() => {
    if (!visible) return;
    setRolesLoading(true);
    setRoleLoadError(false);
    getAssignableRoleOptions()
      .then((list) => {
        const options = list.map(r => ({ value: Number(r.id), label: r.name ?? r.code }));
        const present = new Set(options.map(option => option.value));
        for (const role of user?.roles ?? []) {
          if (!present.has(Number(role.id))) options.push({ value: Number(role.id), label: `${role.name}（已分配，非当前可选角色）`, disabled: true });
        }
        setRoleOptions(options);
      })
      .catch(() => {
        setRoleOptions([]);
        setRoleLoadError(true);
        message.error('角色加载失败，请重试');
      })
      .finally(() => setRolesLoading(false));
  }, [visible, user]);

  useEffect(() => {
    if (visible && user) {
      form.setFieldsValue({
        username: user.username,
        realName: user.realName,
        nickname: user.nickname,
        gender: user.gender,
        phone: user.phone,
        email: user.email,
        orgId: user.orgId,
        status: user.status,
        intro: user.intro,
        roleIds: (user.roles ?? []).map((r) => Number(r.id)).filter(Boolean),
      });
    } else if (visible) {
      form.resetFields();
      form.setFieldsValue({
        gender: UserGender.SECRET,
        status: UserStatus.NORMAL,
      });
    }
  }, [visible, user, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);

      const data: UserFormData = {
        username: values.username,
        realName: values.realName,
        nickname: values.nickname,
        gender: values.gender,
        phone: values.phone,
        email: values.email,
        orgId: values.orgId,
        status: values.status,
        intro: values.intro,
        roleIds: values.roleIds ?? [],
      };

      if (!isEdit && values.password) {
        data.password = values.password;
      }

      if (isEdit) {
        await updateUser(user!.id, data);
        message.success('更新成功');
      } else {
        await createUser(data);
        message.success('创建成功');
      }

      onSuccess();
    } catch (error: unknown) {
      // 仅在非表单校验错误时提示
      if (typeof error === 'object' && error !== null && 'errorFields' in error) {
        return;
      }
      message.error(error instanceof Error ? error.message : '操作失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Modal
      title={isEdit ? '编辑用户' : '新增用户'}
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={submitLoading}
      okButtonProps={{ disabled: roleLoadError }}
      width={600}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          name="username"
          label="用户名"
          rules={[
            { required: true, message: '请输入用户名' },
            { min: 3, max: 30, message: '用户名长度为3-30个字符' },
            { pattern: /^[A-Za-z0-9_]+$/, message: '只能包含字母、数字和下划线' },
          ]}
        >
          <Input placeholder="请输入用户名" maxLength={30} disabled={isEdit} />
        </Form.Item>

        <Form.Item
          name="realName"
          label="真实姓名"
          rules={[
            { required: true, message: '请输入真实姓名' },
            { max: 20, message: '真实姓名不能超过20个字符' },
          ]}
        >
          <Input placeholder="请输入真实姓名" maxLength={20} />
        </Form.Item>

        <Form.Item
          name="nickname"
          label="昵称"
          rules={[{ max: 30, message: '昵称不能超过30个字符' }]}
        >
          <Input placeholder="请输入昵称（选填）" maxLength={30} />
        </Form.Item>

        <Form.Item
          name="gender"
          label="性别"
          rules={[{ required: true, message: '请选择性别' }]}
        >
          <Radio.Group>
            <Radio value={UserGender.SECRET}>保密</Radio>
            <Radio value={UserGender.MALE}>男</Radio>
            <Radio value={UserGender.FEMALE}>女</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="phone"
          label="手机号"
          rules={[
            { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
          ]}
        >
          <Input placeholder="请输入手机号（选填）" maxLength={11} />
        </Form.Item>

        <Form.Item
          name="email"
          label="邮箱"
          rules={[
            { type: 'email', message: '请输入正确的邮箱地址' },
          ]}
        >
          <Input placeholder="请输入邮箱（选填）" maxLength={50} />
        </Form.Item>

        {orgTreeData.length > 0 && (
          <Form.Item name="orgId" label="部门">
            <TreeSelect
              treeData={orgTreeData}
              placeholder="请选择部门（选填）"
              allowClear
              treeDefaultExpandAll
              showSearch
              treeNodeFilterProp="title"
            />
          </Form.Item>
        )}

        {!isEdit && (
          <>
            <Form.Item
              name="password"
              label="密码"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, max: 32, message: '密码长度为6-32个字符' },
              ]}
            >
              <Input.Password placeholder="请输入密码" maxLength={32} />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="确认密码"
              dependencies={['password']}
              rules={[
                { required: true, message: '请确认密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="请再次输入密码" maxLength={32} />
            </Form.Item>
          </>
        )}

        <Form.Item name="roleIds" label="角色">
          <Select
            mode="multiple"
            placeholder="请选择角色（可多选，员工账号选内部角色）"
            optionFilterProp="label"
            allowClear
            loading={rolesLoading}
            notFoundContent={roleLoadError ? '角色加载失败，请关闭后重试' : '暂无可用角色'}
            options={roleOptions}
          />
        </Form.Item>

        <Form.Item
          name="status"
          label="状态"
          rules={[{ required: true, message: '请选择状态' }]}
        >
          <Select placeholder="请选择状态">
            <Select.Option value={UserStatus.NORMAL}>启用</Select.Option>
            <Select.Option value={UserStatus.DISABLED}>禁用</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="intro"
          label="简介"
          rules={[{ max: 200, message: '简介不能超过200个字符' }]}
        >
          <Input.TextArea rows={3} placeholder="请输入简介（选填）" maxLength={200} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default UserForm;
